import type { QuoteItem, QuoteOrderLine, ServiceItem } from '../types';
import type { FlatInclusionDecision } from './flatRateLogic';
import type { SupplementMapping, SupplementPlan, SupplementPlanStep, SupplementTarget } from './quoteSupplementLogic';
import type { AcceptSupplementValues, SupplementProjectOption } from './quoteSupplementService';
import { auftragswertCentsAusAngebot } from './erpSteering';
import { formatEur, formatQty, orderableQuoteItems } from './quoteOrderTranches';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * NACHTRAGSANGEBOT — Formularlogik des Annahmedialogs (rein)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Was SupplementAcceptForm und ProjectPositionPicker anzeigen und prüfen,
 * ohne Netz und ohne Uhr: Optionen der Zuordnung, Zeilen der Zuordnungstabelle,
 * Vorschlag für den Auftragswert, Sperrgründe des Bestätigen-Knopfs und die
 * Werte, die an den Editor gehen. Gerechnet wird der Plan selbst in
 * quoteSupplementLogic (planSupplementForProjectLv) — hier nur, was das
 * Formular daraus macht.
 */

const num = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** Was das Formular an den Aufrufer gibt — `actor` ergänzt der Editor (wer annimmt). */
export type SupplementFormValues = Omit<AcceptSupplementValues, 'actor'>;

// ─── Zuordnung: Wert im Auswahlfeld ⇄ SupplementTarget ─────────────────────

/** Auswahlwert „Neue Position". */
export const APPEND_OPTION = 'append';
const SERVICE_PREFIX = 'svc:';

export const targetToOption = (t: SupplementTarget | null | undefined): string =>
    t?.kind === 'increase' && t.serviceId ? `${SERVICE_PREFIX}${t.serviceId}` : APPEND_OPTION;

export const optionToTarget = (value: string): SupplementTarget =>
    value.startsWith(SERVICE_PREFIX) && value.length > SERVICE_PREFIX.length
        ? { kind: 'increase', serviceId: value.slice(SERVICE_PREFIX.length) }
        : { kind: 'append' };

/**
 * Eine Projektposition im Auswahlfeld:
 * „1.2 Begehung SiGeKo — Vertrag 20 Stk, 300,00 €". NEP-Positionen sind
 * gekennzeichnet — ihre Menge ist im Projekt nicht beauftragt.
 */
export const projectServiceLabel = (
    svc: Pick<ServiceItem, 'lvPosition' | 'title' | 'unit' | 'quantityContracted' | 'unitPrice' | 'is_optional'>,
): string => {
    const head = [(svc.lvPosition || '').trim(), (svc.title || '').trim() || 'ohne Bezeichnung'].filter(Boolean).join(' ');
    const qty = [formatQty(num(svc.quantityContracted)), (svc.unit || '').trim()].filter(Boolean).join(' ');
    return `${head}${svc.is_optional ? ' (NEP)' : ''} — Vertrag ${qty}, ${formatEur(num(svc.unitPrice))}`;
};

export interface TargetOption {
    value: string;
    label: string;
}

/** Optionen je Zeile der Zuordnungstabelle: zuerst „Neue Position", dann das Projekt-LV in seiner Reihenfolge. */
export const supplementTargetOptions = (services: ServiceItem[] | null | undefined): TargetOption[] => [
    { value: APPEND_OPTION, label: 'Neue Position' },
    ...(services || []).map((s) => ({ value: targetToOption({ kind: 'increase', serviceId: s.id }), label: projectServiceLabel(s) })),
];

/** Eine Zeile der Zuordnungstabelle. */
export interface SupplementMappingRow {
    item: QuoteItem;
    /** Menge, die mit diesem Auftrag beauftragt wird. */
    quantity: number;
    /** Aktueller Auswahlwert (targetToOption). */
    value: string;
    /** Zugeordnete Projektposition steht nicht (mehr) im Projekt — sperrt das Eintragen. */
    targetMissing: boolean;
}

/**
 * Zeilen der Zuordnungstabelle: jede beauftragbare Position (aktiv, nicht
 * NEP), die mit den gewählten Zeilen eine Menge > 0 bekommt — in der
 * Reihenfolge des Angebots. Nicht beauftragte Positionen brauchen keine
 * Zuordnung; NEP-Positionen werden ohnehin als Eventualposition angehängt.
 */
export const supplementMappingRows = (
    items: QuoteItem[] | null | undefined,
    lines: QuoteOrderLine[],
    mapping: SupplementMapping,
    services: ServiceItem[] | null | undefined,
): SupplementMappingRow[] => {
    const qty = new Map<string, number>();
    for (const l of lines) qty.set(l.itemId, (qty.get(l.itemId) || 0) + num(l.quantity));
    const ids = new Set((services || []).map((s) => s.id));
    return orderableQuoteItems(items)
        .filter((it) => (qty.get(it.id) || 0) > 0)
        .map((it) => {
            const t = mapping[it.id];
            return {
                item: it,
                quantity: qty.get(it.id) || 0,
                value: targetToOption(t),
                targetMissing: t?.kind === 'increase' && !ids.has(t.serviceId),
            };
        });
};

/** Zuordnung einer Zeile ändern — neues Objekt, die übrigen Einträge bleiben. */
export const setMappingTarget = (mapping: SupplementMapping, itemId: string, value: string): SupplementMapping => ({
    ...mapping,
    [itemId]: optionToTarget(value),
});

// ─── Vorschau ───────────────────────────────────────────────────────────────

/**
 * Hauptzeile eines Plan-Schritts (LV-Nr. zeigt das Formular davor):
 * Erhöhung „Begehung SiGeKo: 20 → 40 Stk", neue Position
 * „Baustellenordnung: neu, 1 psch.".
 */
export const supplementStepText = (
    step: Pick<SupplementPlanStep, 'kind' | 'title' | 'unit' | 'from' | 'to'>,
): string => {
    const unit = (step.unit || '').trim();
    const title = (step.title || '').trim() || 'Position';
    return step.kind === 'increase'
        ? `${title}: ${formatQty(step.from)} → ${formatQty(step.to)}${unit ? ` ${unit}` : ''}`
        : `${title}: neu, ${formatQty(step.to)}${unit ? ` ${unit}` : ''}`;
};

/**
 * Hinweis, wenn eine gewählte Erhöhung zur neuen Position wird:
 * „Einzelpreis 300 € im Projekt, 320 € im Nachtrag → neue Position N1.1".
 * null = kein erzwungenes Anhängen.
 */
export const forcedAppendText = (step: Pick<SupplementPlanStep, 'forcedAppendReason' | 'lvPosition'>): string | null => {
    const reason = (step.forcedAppendReason || '').trim();
    if (!reason) return null;
    const lv = (step.lvPosition || '').trim();
    return lv ? `${reason} → neue Position ${lv}` : `${reason} → neue Position`;
};

/**
 * id der NEU angehängten Projektposition eines Schritts — für die Frage
 * „in der Monatspauschale enthalten?". Nach Vertrag trägt eine angehängte
 * Position die id der Angebotsposition; liefert der Plan eine eigene
 * `serviceId`, gilt die. null bei Erhöhungen und unbekannten ids.
 */
export const newServiceIdOfStep = (
    step: Pick<SupplementPlanStep, 'kind' | 'itemId' | 'serviceId'>,
    newServiceIds: readonly string[],
): string | null => {
    if (step.kind !== 'append') return null;
    if (step.serviceId && newServiceIds.includes(step.serviceId)) return step.serviceId;
    return newServiceIds.includes(step.itemId) ? step.itemId : null;
};

/** Steht der Schritt für eine NEP-Position des Angebots (wird als Eventualposition angehängt)? */
export const isNepStep = (step: Pick<SupplementPlanStep, 'itemId'>, items: QuoteItem[] | null | undefined): boolean =>
    (items || []).some((it) => it.id === step.itemId && it.nep === true);

// ─── Auftragswert ───────────────────────────────────────────────────────────

/**
 * Vorschlag „Auftragswert netto NEU gesamt" in Cent: bisher fixierter Wert +
 * Netto dieses Nachtrags. OHNE fixierten Wert kein Vorschlag (null): der
 * Nachtrag allein ist nicht der Auftragswert des Projekts, ein Vorschlag mit
 * nur seinem Betrag würde den Hauptauftrag still aus dem Auftragsbestand
 * nehmen. Dann trägt der Bearbeiter den Gesamtwert selbst ein oder lässt das
 * Feld leer (= unverändert).
 */
export const suggestSupplementOrderValueCents = (
    priorCents: number | null | undefined,
    supplementNet: number,
): number | null => {
    if (priorCents == null || !Number.isFinite(Number(priorCents))) return null;
    return Math.max(0, Math.round(Number(priorCents))) + auftragswertCentsAusAngebot(supplementNet);
};

// ─── Projektauswahl ─────────────────────────────────────────────────────────

/** „26031030 Neubau Halle" — fehlende Teile entfallen. */
export const supplementProjectLabel = (p: Pick<SupplementProjectOption, 'projectNumber' | 'projectName'>): string =>
    [(p.projectNumber || '').trim(), (p.projectName || '').trim()].filter(Boolean).join(' ') || 'Projekt ohne Nummer';

/**
 * Warnung, wenn das Projekt einem anderen Kunden gehört als das Angebot.
 * Nur Hinweis — Projekte wechseln gelegentlich den Rechnungsempfänger.
 */
export const supplementCustomerHint = (
    projectCustomerId: string | null | undefined,
    quoteCustomerId: string | null | undefined,
): string | null =>
    projectCustomerId && quoteCustomerId && projectCustomerId !== quoteCustomerId
        ? 'Das Projekt ist einem anderen Kunden zugeordnet als dieses Angebot — bitte prüfen, ob der Nachtrag dorthin gehört.'
        : null;

// ─── Bestätigen ─────────────────────────────────────────────────────────────

export interface SupplementBlockerInput {
    projectId: string;
    /** Kontext des Projekts geladen (loadSupplementContext). */
    ctxLoaded: boolean;
    ctxError?: string;
    /** Archiv/Tochter — aus dem Kontext. */
    ctxBlocker?: string;
    lines: QuoteOrderLine[];
    /** Prüfung der Teilbeauftragung (validateOrderLines), leer bei „vollständig". */
    scopeErrors: string[];
    /** Angehakte Zeile mit unlesbarer Menge. */
    unreadableQty: boolean;
    plan: Pick<SupplementPlan, 'missing' | 'missingTargets' | 'steps'> | null;
    /** Der Plan ließ sich nicht rechnen. */
    planError?: string;
    /** Neue Positionen ohne Entscheidung zur Monatspauschale. */
    pendingFlat: string[];
}

/**
 * Lücken des Plans, die das Eintragen sperren: Zeilen ohne Angebotsposition
 * und Zuordnungen auf Projektpositionen, die es nicht (mehr) gibt.
 * Paketsperren stehen am Schritt selbst (packageBlock).
 */
export const supplementPlanProblems = (plan: Pick<SupplementPlan, 'missing' | 'missingTargets'>): string[] => {
    const out: string[] = [];
    if (plan.missing.length > 0) {
        out.push(`${plan.missing.length === 1 ? 'Eine Zeile gehört' : `${plan.missing.length} Zeilen gehören`} zu keiner Angebotsposition — bitte das Angebot neu laden.`);
    }
    if (plan.missingTargets.length > 0) {
        out.push(`${plan.missingTargets.length === 1 ? 'Eine zugeordnete Projektposition existiert' : `${plan.missingTargets.length} zugeordnete Projektpositionen existieren`} nicht mehr — bitte neu zuordnen.`);
    }
    return out;
};

/**
 * Warum „Nachtrag eintragen" (noch) gesperrt ist — leer = frei. Der erste
 * Grund steht am Knopf; der Dienst prüft Sperren (Archiv, Tochter, Pauschale)
 * vor dem Schreiben noch einmal.
 */
export const supplementConfirmBlockers = (i: SupplementBlockerInput): string[] => {
    if (!i.projectId) return ['Bitte das Projekt wählen, zu dem der Nachtrag gehört.'];
    const out: string[] = [];
    if (i.ctxError) out.push(i.ctxError);
    else if (!i.ctxLoaded) out.push('Projekt wird geladen …');
    if (i.ctxBlocker) out.push(i.ctxBlocker);
    if (i.lines.length === 0) out.push('Bitte mindestens eine Position mit Menge wählen.');
    else if (i.scopeErrors.length > 0) out.push(...i.scopeErrors);
    if (i.unreadableQty) out.push('Eine angehakte Menge ist keine gültige Zahl.');
    if (i.planError) out.push(i.planError);
    if (i.plan) {
        out.push(...supplementPlanProblems(i.plan));
        for (const s of i.plan.steps) if (s.packageBlock) out.push(s.packageBlock);
    } else if (i.ctxLoaded && !i.ctxError && i.lines.length > 0 && i.scopeErrors.length === 0 && !i.planError) {
        // Ohne Plan keine Vorschau — und ohne Vorschau kein Eintragen.
        out.push('Die Vorschau ist noch nicht berechnet.');
    }
    if (i.pendingFlat.length > 0) out.push('Für jede neue Position bitte entscheiden: in der Monatspauschale enthalten — ja oder nein.');
    return out;
};

export interface SupplementFormInput {
    projectId: string;
    mapping: SupplementMapping;
    scope: 'full' | 'partial';
    partialLines: QuoteOrderLine[];
    orderDate: string;
    customerRef: string;
    flatDecisions: Partial<Record<string, FlatInclusionDecision>>;
    /** ids der neu angehängten Positionen im aktuellen Plan. */
    newServiceIds: readonly string[];
    endDate: string;
    /** Projektende laut Projekt — unverändert = nicht mitschicken. */
    ctxEndDate?: string;
    /** Auftragswert aus dem Feld; null = leer bzw. unlesbar. */
    orderValueCents: number | null;
    /**
     * Auftragswert laut Projekt beim Öffnen (SupplementContext.orderValueCents;
     * null = keiner) — die Basis, aus der der Gesamtwert gerechnet wurde.
     */
    ctxOrderValueCents?: number | null;
}

/**
 * Werte für acceptQuoteAsSupplement (ohne actor):
 *  - ohne `orderLines` = vollständig (alle offenen Mengen),
 *  - Monatspauschale nur für Positionen, die der aktuelle Plan neu anhängt
 *    (Entscheidungen aus einer früheren Zuordnung fallen weg),
 *  - Projektende nur, wenn es sich gegenüber dem Projekt ändert,
 *  - Auftragswert nur > 0 (leer/0 = nicht ändern), dann mit der Basis, die
 *    der Dialog sah — der Dienst schlägt bei inzwischen geändertem Wert nur
 *    den Zuwachs auf (zwei parallele Nachträge verlieren keinen).
 */
export const buildSupplementFormValues = (i: SupplementFormInput): SupplementFormValues => {
    const flatDecisions: Partial<Record<string, FlatInclusionDecision>> = {};
    for (const id of i.newServiceIds) {
        const d = i.flatDecisions[id];
        if (d === 'included' || d === 'excluded') flatDecisions[id] = d;
    }
    const endDate = (i.endDate || '').slice(0, 10);
    const orderValueCents = i.orderValueCents != null && i.orderValueCents > 0 ? i.orderValueCents : undefined;
    return {
        projectId: i.projectId,
        mapping: i.mapping,
        orderLines: i.scope === 'partial' ? i.partialLines : undefined,
        orderDate: i.orderDate || undefined,
        customerRef: i.customerRef.trim() || undefined,
        flatDecisions,
        endDate: endDate && endDate !== (i.ctxEndDate || '').slice(0, 10) ? endDate : undefined,
        orderValueCents,
        ...(orderValueCents !== undefined ? { orderValueBaseCents: i.ctxOrderValueCents ?? null } : {}),
    };
};

// ─── Projektposition übernehmen (Editor) ────────────────────────────────────

/** Projektpositionen, auf die schon eine Position dieses Angebots zeigt (targetServiceId). */
export const takenProjectServiceIds = (items: QuoteItem[] | null | undefined): Set<string> =>
    new Set((items || []).map((it) => it.targetServiceId).filter((id): id is string => !!id));
